/**
 * Ocucalm — interactive cart drawer (Ciao Totes pattern).
 *
 * <cart-drawer> : slide-out panel rendered server-side by
 * sections/cart-drawer.liquid. Every cart mutation re-renders the whole
 * section via the Section Rendering API (`?sections=cart-drawer`), so all
 * numbers (free-shipping gap, totals) stay Liquid-computed.
 *
 * <cart-drawer-suggestions> : while free shipping isn't unlocked, suggests
 * products from the WHOLE catalog, fetched client-side via
 * /collections/all/products.json (Liquid caps collections at 50 products and
 * doesn't expose the automatic "all" collection). Cached in sessionStorage
 * (TTL 30 min), keyed by locale root since the localized endpoint returns
 * translated titles and handles. Sort: price >= remaining ascending first
 * (closest above the gap first), then price < remaining descending.
 */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';

  function localeRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function cartChangeUrl() {
    var base = (window.theme && theme.routes && theme.routes.cart_change_url) || '/cart/change';
    return base + '.js';
  }

  function cartAddUrl() {
    var url = (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js';
    return url.indexOf('.js') === -1 ? url + '.js' : url;
  }

  function dispatchCartChanged() {
    document.documentElement.dispatchEvent(
      new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false })
    );
  }

  /* ------------------------------------------------------------------ */
  /* <cart-drawer>                                                       */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('cart-drawer')) {
    var CartDrawer = /** @class */ (function () {
      class CartDrawer extends HTMLElement {
        connectedCallback() {
          if (this._bound) return;
          this._bound = true;
          this._suppressUntil = 0;
          this._busy = false;

          this.addEventListener('click', this.onClick.bind(this));
          this.addEventListener('change', this.onQtyChange.bind(this));

          document.addEventListener('keyup', function (evt) {
            if (evt.key === 'Escape' && this.classList.contains('is-open')) this.close();
          }.bind(this));

          // Header cart icon opens the drawer everywhere except the cart page.
          document.addEventListener('click', function (evt) {
            var link = evt.target.closest('a.cart-link');
            if (!link) return;
            if (document.body.classList.contains('template-cart')) return;
            evt.preventDefault();
            this.open();
          }.bind(this));

          // Re-render when the cart changes elsewhere (cart page, header,
          // gift auto-add...). Skipped when the change came from the drawer
          // itself — those paths already rendered the fresh section HTML.
          document.addEventListener('theme:cartchanged', function () {
            setTimeout(function () {
              if (Date.now() < this._suppressUntil) return;
              this.refresh();
            }.bind(this), 300);
          }.bind(this));
        }

        open() {
          this.classList.add('is-open');
          document.documentElement.classList.add('cart-drawer-open');
          document.dispatchEvent(new CustomEvent('cart-drawer:opened'));
          var panel = this.querySelector('.cart-drawer__panel');
          if (panel) panel.focus({ preventScroll: true });
          // Catch mutations made without an event (e.g. auto-added gifts),
          // unless the content was just rendered fresh.
          if (Date.now() - (this._lastRenderAt || 0) > 1000) this.refresh();
        }

        close() {
          this.classList.remove('is-open');
          document.documentElement.classList.remove('cart-drawer-open');
        }

        renderContents(sectionHtml) {
          var holder = document.createElement('div');
          holder.innerHTML = sectionHtml;
          var fresh = holder.querySelector('.cart-drawer__inner');
          var current = this.querySelector('.cart-drawer__inner');
          if (!fresh || !current) return;
          current.replaceWith(fresh);
          var freshRoot = holder.querySelector('cart-drawer');
          this.classList.toggle('cart-drawer--empty', !!(freshRoot && freshRoot.classList.contains('cart-drawer--empty')));
          this._suppressUntil = Date.now() + 800;
          this._lastRenderAt = Date.now();
        }

        refresh() {
          if (this._refreshing) return;
          this._refreshing = true;
          fetch(localeRoot() + '?sections=' + SECTION_ID)
            .then(function (response) { return response.json(); })
            .then(function (sections) {
              if (sections && sections[SECTION_ID]) this.renderContents(sections[SECTION_ID]);
            }.bind(this))
            .catch(function (error) { console.error('[cart-drawer] refresh failed:', error); })
            .finally(function () { this._refreshing = false; }.bind(this));
        }

        changeLine(lineKey, quantity) {
          if (this._busy) return;
          this._busy = true;
          this.classList.add('is-loading');
          fetch(cartChangeUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: lineKey, quantity: quantity, sections: SECTION_ID })
          })
            .then(function (response) { return response.json(); })
            .then(function (data) {
              if (data && data.sections && data.sections[SECTION_ID]) {
                this.renderContents(data.sections[SECTION_ID]);
              }
              dispatchCartChanged();
            }.bind(this))
            .catch(function (error) {
              console.error('[cart-drawer] update failed:', error);
              this.refresh();
            }.bind(this))
            .finally(function () {
              this._busy = false;
              this.classList.remove('is-loading');
            }.bind(this));
        }

        onClick(evt) {
          if (evt.target.closest('[data-cart-drawer-close]')) {
            evt.preventDefault();
            this.close();
            return;
          }

          var removeBtn = evt.target.closest('[data-cart-remove]');
          if (removeBtn) {
            evt.preventDefault();
            this.changeLine(removeBtn.getAttribute('data-line-key'), 0);
            return;
          }

          var stepBtn = evt.target.closest('[data-cart-qty-minus], [data-cart-qty-plus]');
          if (stepBtn) {
            evt.preventDefault();
            var input = stepBtn.closest('.cart-drawer-item__qty').querySelector('[data-cart-qty-input]');
            if (!input) return;
            var qty = parseInt(input.value, 10) || 0;
            qty = stepBtn.hasAttribute('data-cart-qty-minus') ? qty - 1 : qty + 1;
            if (qty < 0) qty = 0;
            this.changeLine(input.getAttribute('data-line-key'), qty);
          }
        }

        onQtyChange(evt) {
          var input = evt.target.closest('[data-cart-qty-input]');
          if (!input) return;
          var qty = parseInt(input.value, 10);
          if (isNaN(qty) || qty < 0) return;
          this.changeLine(input.getAttribute('data-line-key'), qty);
        }
      }
      return CartDrawer;
    })();

    customElements.define('cart-drawer', CartDrawer);
  }

  /* ------------------------------------------------------------------ */
  /* <cart-drawer-suggestions>                                           */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('cart-drawer-suggestions')) {
    var CACHE_KEY = 'ocucalm:suggestions-catalog:v1';
    var CACHE_TTL_MS = 30 * 60 * 1000;
    var catalogPromise = null;

    function slimProduct(product) {
      var variants = (product.variants || []).map(function (variant) {
        return {
          id: variant.id,
          available: !!variant.available,
          priceCents: Math.round(parseFloat(variant.price) * 100),
          compareAtCents: variant.compare_at_price
            ? Math.round(parseFloat(variant.compare_at_price) * 100)
            : null,
          requiresShipping: variant.requires_shipping !== false
        };
      });
      var buyable = variants.filter(function (variant) { return variant.available; });
      var priceCents = (buyable.length ? buyable : variants).reduce(function (min, variant) {
        return Math.min(min, variant.priceCents);
      }, Infinity);
      return {
        id: product.id,
        title: product.title,
        handle: product.handle,
        type: product.product_type || '',
        tags: product.tags || [],
        image: (product.images && product.images[0] && product.images[0].src) || null,
        variants: variants,
        priceCents: isFinite(priceCents) ? priceCents : 0
      };
    }

    function loadCatalog() {
      if (catalogPromise) return catalogPromise;
      var cacheKey = CACHE_KEY + ':' + localeRoot();
      try {
        var cached = JSON.parse(sessionStorage.getItem(cacheKey));
        if (cached && Date.now() - cached.ts < CACHE_TTL_MS && Array.isArray(cached.products)) {
          catalogPromise = Promise.resolve(cached.products);
          return catalogPromise;
        }
      } catch (error) { /* unreadable cache: refetch */ }

      catalogPromise = (async function () {
        var products = [];
        // Pagination guard: 10 pages x 250 = 2500 products max.
        for (var page = 1; page <= 10; page++) {
          var response = await fetch(localeRoot() + 'collections/all/products.json?limit=250&page=' + page);
          if (!response.ok) throw new Error('HTTP ' + response.status);
          var batch = (await response.json()).products || [];
          products = products.concat(batch.map(slimProduct));
          if (batch.length < 250) break;
        }
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), products: products }));
        } catch (error) { /* storage full: memory cache is enough */ }
        return products;
      })();
      // A network failure must not poison the cache: allow retry.
      catalogPromise.catch(function () { catalogPromise = null; });
      return catalogPromise;
    }

    class CartDrawerSuggestions extends HTMLElement {
      connectedCallback() {
        this.remainingCents = parseInt(this.dataset.remainingCents, 10) || 0;
        this.maxSlides = parseInt(this.dataset.max, 10) || 8;
        this.excludeTag = (this.dataset.excludeTag || 'exclu-suggestions').toLowerCase();
        this.cartProductIds = (this.dataset.cartProductIds || '')
          .split(',')
          .filter(Boolean)
          .map(Number);
        this.list = this.querySelector('[data-suggestions-list]');
        if (!this.list || this.remainingCents <= 0) return;

        this.addEventListener('click', this.onClick.bind(this));

        // Catalogue paresseux : l'élément se (re)construit avec chaque page,
        // et déclenchait le téléchargement du catalogue complet pour tous les
        // visiteurs — même ceux qui n'ouvrent jamais leur panier. On attend
        // la première ouverture du tiroir avant tout appel réseau.
        if (document.documentElement.classList.contains('cart-drawer-open')) {
          this.build();
        } else {
          this._onDrawerOpen = this.build.bind(this);
          document.addEventListener('cart-drawer:opened', this._onDrawerOpen, { once: true });
        }
      }

      disconnectedCallback() {
        if (this._onDrawerOpen) {
          document.removeEventListener('cart-drawer:opened', this._onDrawerOpen);
          this._onDrawerOpen = null;
        }
      }

      build() {
        if (this._built) return;
        this._built = true;
        loadCatalog()
          .then(function (products) { this.render(products); }.bind(this))
          .catch(function (error) { console.error('[cart-suggestions] catalog unavailable:', error); });
      }

      candidates(products) {
        var self = this;
        return products.filter(function (product) {
          if (self.cartProductIds.indexOf(product.id) !== -1) return false;
          if (!product.variants.some(function (variant) { return variant.available; })) return false;
          if (product.tags.some(function (tag) { return tag.toLowerCase() === self.excludeTag; })) return false;
          // Gift cards: type "Gift Cards" or no-shipping products.
          if (/gift.?card|carte.?cadeau/i.test(product.type + ' ' + product.handle)) return false;
          if (product.variants.every(function (variant) { return !variant.requiresShipping; })) return false;
          return true;
        });
      }

      select(pool) {
        var remaining = this.remainingCents;
        var above = pool
          .filter(function (product) { return product.priceCents >= remaining; })
          .sort(function (a, b) { return a.priceCents - b.priceCents; });
        var below = pool
          .filter(function (product) { return product.priceCents < remaining; })
          .sort(function (a, b) { return b.priceCents - a.priceCents; });
        return above.concat(below).slice(0, this.maxSlides);
      }

      render(products) {
        var ordered = this.select(this.candidates(products));
        if (!ordered.length) return;
        this.list.innerHTML = ordered.map(this.slideHtml.bind(this)).join('');
        this.removeAttribute('hidden');
        this.classList.toggle('cart-drawer-suggestions--single', ordered.length < 2);
      }

      slideHtml(product) {
        var url = localeRoot() + 'products/' + this.escape(product.handle);
        return (
          '<li class="cart-drawer-suggestion">' +
            '<a href="' + url + '" class="cart-drawer-suggestion__media" tabindex="-1" aria-hidden="true">' + this.imageHtml(product) + '</a>' +
            '<div class="cart-drawer-suggestion__info">' +
              '<a href="' + url + '" class="cart-drawer-suggestion__title">' + this.escape(product.title) + '</a>' +
              this.priceHtml(product) +
            '</div>' +
            this.buttonHtml(product, url) +
          '</li>'
        );
      }

      imageHtml(product) {
        if (!product.image) return '';
        var sep = product.image.indexOf('?') !== -1 ? '&' : '?';
        var src = this.escape(product.image);
        return '<img src="' + src + sep + 'width=160" alt="' + this.escape(product.title) + '" loading="lazy" width="80" height="80">';
      }

      priceHtml(product) {
        var variant = product.variants.filter(function (item) {
          return item.available && item.priceCents === product.priceCents;
        })[0] || product.variants[0];
        if (variant.compareAtCents && variant.compareAtCents > variant.priceCents) {
          return '<div class="cart-drawer-suggestion__price"><s class="theme-money">' + this.money(variant.compareAtCents) + '</s> <span class="theme-money cart-drawer-suggestion__price-sale">' + this.money(variant.priceCents) + '</span></div>';
        }
        return '<div class="cart-drawer-suggestion__price"><span class="theme-money">' + this.money(variant.priceCents) + '</span></div>';
      }

      buttonHtml(product, url) {
        // Multiple variants: link to the product page instead of guessing.
        if (product.variants.length > 1) {
          return '<a href="' + url + '" class="cart-drawer-suggestion__button cart-drawer-suggestion__button--link">' + this.escape(this.dataset.labelChoose) + '</a>';
        }
        var variant = product.variants[0];
        return '<button type="button" class="cart-drawer-suggestion__button" data-suggestion-add data-variant-id="' + variant.id + '">' + this.escape(this.dataset.labelAdd) + '</button>';
      }

      onClick(evt) {
        var prev = evt.target.closest('[data-suggestions-prev]');
        var next = evt.target.closest('[data-suggestions-next]');
        if (prev || next) {
          evt.preventDefault();
          var step = this.list.clientWidth;
          this.list.scrollBy({ left: prev ? -step : step, behavior: 'smooth' });
          return;
        }

        var addBtn = evt.target.closest('[data-suggestion-add]');
        if (!addBtn) return;
        evt.preventDefault();
        addBtn.disabled = true;
        addBtn.classList.add('is-loading');

        var body = new URLSearchParams();
        body.append('id', addBtn.getAttribute('data-variant-id'));
        body.append('quantity', '1');
        body.append('sections', SECTION_ID);

        fetch(cartAddUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
          body: body.toString()
        })
          .then(function (response) { return response.json(); })
          .then(function (data) {
            var drawer = document.querySelector('cart-drawer');
            if (drawer && data && data.sections && data.sections[SECTION_ID]) {
              drawer.renderContents(data.sections[SECTION_ID]);
            }
            dispatchCartChanged();
          })
          .catch(function (error) {
            console.error('[cart-suggestions] add failed:', error);
            addBtn.disabled = false;
            addBtn.classList.remove('is-loading');
          });
      }

      /* Reuses the theme's Liquid-compatible money formatter when present,
         with a minimal port as fallback. */
      money(cents) {
        var format = this.dataset.moneyFormat || '${{amount}}';
        if (window.theme && theme.Shopify && typeof theme.Shopify.formatMoney === 'function') {
          return theme.Shopify.formatMoney(cents, format);
        }
        var amount = (cents / 100).toFixed(2);
        return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
      }

      escape(text) {
        var div = document.createElement('div');
        div.textContent = text == null ? '' : String(text);
        return div.innerHTML.replace(/"/g, '&quot;');
      }
    }

    customElements.define('cart-drawer-suggestions', CartDrawerSuggestions);
  }

  /* ------------------------------------------------------------------ */
  /* AJAX add-to-cart forms (form[data-bundle-add]): the "trousses" kit  */
  /* pages (multi-item) and the product-card add buttons (single item)   */
  /* both use items[][id] inputs — items are added via AJAX and the      */
  /* drawer opens instead of navigating to the cart page. Falls back to  */
  /* the normal POST when the drawer isn't on the page or AJAX fails.    */
  /* ------------------------------------------------------------------ */
  document.addEventListener('submit', function (evt) {
    var form = evt.target.closest('form[data-bundle-add]');
    if (!form) return;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') return;
    evt.preventDefault();

    var idInputs = form.querySelectorAll('input[name="items[][id]"]');
    var qtyInputs = form.querySelectorAll('input[name="items[][quantity]"]');
    var merged = {};
    idInputs.forEach(function (input, i) {
      var id = parseInt(input.value, 10);
      if (!id) return;
      var qty = qtyInputs[i] ? parseInt(qtyInputs[i].value, 10) || 1 : 1;
      merged[id] = (merged[id] || 0) + qty;
    });
    var items = Object.keys(merged).map(function (id) {
      return { id: parseInt(id, 10), quantity: merged[id] };
    });
    if (!items.length) return;

    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items, sections: SECTION_ID })
    })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        if (data && data.status && data.description) {
          alert(data.description);
          return;
        }
        if (data && data.sections && data.sections[SECTION_ID]) {
          drawer.renderContents(data.sections[SECTION_ID]);
        }
        drawer.open();
        dispatchCartChanged();
      })
      .catch(function (error) {
        console.error('[bundle-add] AJAX failed, falling back to full submit:', error);
        form.submit();
      })
      .finally(function () {
        if (submitBtn) submitBtn.disabled = false;
      });
  });
})();